import { useFloating } from '@floating-ui/react-dom-interactions';
import Input from '@shared/components/InputGroup/Input';
import cx from 'classnames';
import moment from 'moment';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as ArrowDown } from 'assets/icons/ArrowDown.svg';
import { getDefaultDateInputFloatingProps } from 'components/DateInput/utils';
import useHideOnOutsideClick from 'hooks/useHideOnOutsideClick';

const MINUTES_PER_HOUR = 60;
const STEP_MIN = 15;
const MAX_STEP_MIN = 23 * 60 + 45; // 23h 45m absolute cap

interface DurationTimePickerProps {
  startTime: Date | null;
  endDate?: Date | null;
  value: Date | null;
  onChange: (newEndTime: Date) => void;
  name: string;
  placeholder?: string;
  openSignal?: number;
  containerClassName?: string;
}

interface Option {
  time: Date;
  minutesFromStart: number;
  label: string;
}

const DurationTimePicker = ({
  startTime,
  endDate,
  value,
  onChange,
  name,
  placeholder,
  openSignal,
  containerClassName,
}: DurationTimePickerProps) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const pickerRef = useRef<HTMLDivElement | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const isFirstMount = useRef(true);

  useEffect(() => {
    if (isFirstMount.current) {
      isFirstMount.current = false;
      return;
    }
    if (openSignal !== undefined) {
      setIsOpen(true);
    }
  }, [openSignal]);

  useEffect(() => {
    if (!isOpen || !pickerRef.current) return;
    requestAnimationFrame(() => {
      if (!pickerRef.current) return;
      const selected = pickerRef.current.querySelector<HTMLButtonElement>('[data-selected="true"]');
      selected?.scrollIntoView({ block: 'center', behavior: 'instant' });
    });
  }, [isOpen]);
  // #8107 — `strategy: 'fixed'` keeps the dropdown anchored when the picker sits inside a scrolled
  // / transformed container; `width: 'max-content'` lets options size to their content while
  // `minWidth` keeps it at least as wide as the input.
  const { x, y, reference, floating, strategy } = useFloating({
    ...getDefaultDateInputFloatingProps('bottom'),
    strategy: 'fixed',
  });

  useHideOnOutsideClick({ ref: pickerRef, onClickOutside: () => setIsOpen(false), additionalSelector: `#${name}` });

  const formatOption = (minutesFromStart: number, time: Date): string => {
    const timeLabel = moment(time).format('HH:mm');
    if (minutesFromStart < MINUTES_PER_HOUR) {
      return t('messenger.schedule_meeting_duration_mins', { time: timeLabel, count: minutesFromStart });
    }
    if (minutesFromStart === MINUTES_PER_HOUR) return t('messenger.schedule_meeting_duration_hr', { time: timeLabel });
    if (minutesFromStart % MINUTES_PER_HOUR === 0) {
      const hours = minutesFromStart / MINUTES_PER_HOUR;
      return t('messenger.schedule_meeting_duration_hrs', { time: timeLabel, count: hours });
    }
    const hours = Math.floor(minutesFromStart / MINUTES_PER_HOUR);
    const mins = minutesFromStart % MINUTES_PER_HOUR;
    return t('messenger.schedule_meeting_duration_hr_mins', { time: timeLabel, hours, mins });
  };

  const buildOptions = (): Option[] => {
    if (!startTime) return [];
    const start = moment(startTime);
    const options: Option[] = [];

    // When the end date is a later day than the start, enumerate every 15-min slot of the end day
    // (00:00 to 23:45). Durations in the label reflect minutes from startTime to each slot.
    const isMultiDay = endDate != null && moment(endDate).startOf('day').isAfter(start.clone().startOf('day'));

    if (isMultiDay) {
      const endDayStart = moment(endDate).startOf('day');
      const baseMinutes = endDayStart.diff(start, 'minutes');
      for (let offset = 0; offset <= MAX_STEP_MIN; offset += STEP_MIN) {
        const minutesFromStart = baseMinutes + offset;
        const time = endDayStart.clone().add(offset, 'minutes').toDate();
        options.push({ time, minutesFromStart, label: formatOption(minutesFromStart, time) });
      }
      return options;
    }

    // Same day: options start at startTime + 15min, capped at the last 15-min step STRICTLY before
    // midnight of the start day (absolute 23h 45m).
    const midnight = start.clone().startOf('day').add(1, 'day');
    const minsToMidnight = midnight.diff(start, 'minutes');
    // Subtract 1 before flooring so we exclude midnight itself (e.g. 60 → floor(59/15)*15 = 45)
    const stepsTillMidnight = Math.floor((minsToMidnight - 1) / STEP_MIN) * STEP_MIN;
    const capStep = Math.min(stepsTillMidnight, MAX_STEP_MIN);

    let step = STEP_MIN;
    while (step <= capStep) {
      const time = start.clone().add(step, 'minutes').toDate();
      options.push({ time, minutesFromStart: step, label: formatOption(step, time) });
      step += STEP_MIN;
    }
    return options;
  };

  const options = buildOptions();
  const displayValue = value ? moment(value).format('HH:mm') : '';

  const handleSelect = (option: Option) => {
    onChange(option.time);
    setIsOpen(false);
  };

  const toggleIsOpen = () => {
    if (!startTime) return;
    setIsOpen((prev) => !prev);
  };

  return (
    <div className={cx('w-full xs:w-44', containerClassName)} ref={wrapperRef}>
      <Input
        name={name}
        id={name}
        inputSize="md"
        className={cx(
          'truncate rounded-lg !bg-gray-10 text-sm font-light cursor-pointer',
          !startTime && 'opacity-50 cursor-not-allowed'
        )}
        containerClassName={cx(
          '!border-gray-20 focus-within:!border-primary-50 rounded-2xl text-current',
          isOpen && '!border-primary-50'
        )}
        variant="white-bg"
        autoComplete="off"
        iconClassName={cx('stroke-1 h-5 w-5 stroke-current cursor-pointer', isOpen && 'rotate-180')}
        isRounded
        onContainerClick={toggleIsOpen}
        readOnly
        icon={ArrowDown}
        value={displayValue}
        placeholder={placeholder}
        ref={reference}
      />
      {isOpen && startTime && (
        <div
          ref={(element) => {
            pickerRef.current = element;
            floating(element);
          }}
          style={{
            position: strategy,
            top: y ?? 0,
            left: x ?? 0,
            minWidth: wrapperRef.current?.offsetWidth,
            width: 'max-content',
          }}
          className="z-dropdown flex max-h-80 flex-col gap-1 overflow-y-auto rounded-2xl bg-white p-4 shadow-dropdown"
        >
          {options.map((option) => {
            const isSelected =
              !!value && value.getHours() === option.time.getHours() && value.getMinutes() === option.time.getMinutes();
            return (
              <button
                type="button"
                key={option.minutesFromStart}
                data-selected={isSelected ? 'true' : undefined}
                onClick={() => handleSelect(option)}
                className={cx(
                  'flex min-h-[32px] w-full items-center justify-center whitespace-nowrap px-3 py-1.5 text-size-sm font-light',
                  isSelected ? 'rounded-full bg-primary-50 text-white' : 'rounded-xl text-black hover:bg-gray-10'
                )}
              >
                {option.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default DurationTimePicker;
